import type { SearchResult } from '@/entities/search';

export class SearchCache {
  private cache: SearchResult[] = [];

  add(items: SearchResult[]): void {
    this.cache.push(...items);
  }

  take(count: number): SearchResult[] {
    const taken = this.cache.slice(0, count);
    this.cache = this.cache.slice(count);
    return taken;
  }

  peek(count: number): SearchResult[] {
    return this.cache.slice(0, count);
  }

  size(): number {
    return this.cache.length;
  }

  clear(): void {
    this.cache = [];
  }

  isEmpty(): boolean {
    return this.cache.length === 0;
  }

  getAll(): SearchResult[] {
    return [...this.cache];
  }
}

export const parseDate = (dateStr: string | number | undefined): number => {
  if (!dateStr) return 0;
  if (typeof dateStr === 'number') return dateStr;
  return new Date(dateStr).getTime();
};

export const getUniqueKey = (item: SearchResult): string => {
  const runId = item._source?.rawAssetItem?.run?.id;
  if (runId !== undefined) {
    return `${item.type}-${runId}-${item.id}`;
  }
  return `${item.type}-${item.id}`;
};

export const mergeSearchResults = (
  results1: SearchResult[],
  results2: SearchResult[],
  sortBy: 'name' | 'lastUpdated' | 'lastUpdatedAsc'
): SearchResult[] => {
  const allResults = [...results1, ...results2];

  const uniqueMap = new Map<string, SearchResult>();
  allResults.forEach((item) => {
    const uniqueKey = getUniqueKey(item);
    if (!uniqueMap.has(uniqueKey)) {
      uniqueMap.set(uniqueKey, item);
    }
  });

  const uniqueResults = Array.from(uniqueMap.values());

  return uniqueResults.sort((a, b) => {
    if (sortBy === 'name') {
      const nameA = a._source?.name || a.title;
      const nameB = b._source?.name || b.title;
      return nameA.localeCompare(nameB, 'en-US', { numeric: true, sensitivity: 'base' });
    }
    const timeA = parseDate(a.createdAt) || parseDate(a.updatedAt);
    const timeB = parseDate(b.createdAt) || parseDate(b.updatedAt);
    if (sortBy === 'lastUpdatedAsc') {
      return timeA - timeB;
    }
    return timeB - timeA;
  });
};
